import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './RoadmapDinamico.css';
import { roadmapData } from './roadmapData';
import useSpeech from './hooks/useSpeech';
import useWorkTimer from './hooks/useWorkTimer';
import useActivityLog from './hooks/useActivityLog';
import useDependencies from './hooks/useDependencies';
import Observations from './components/Observations';
import ActivityFeed from './components/ActivityFeed';
import { useAuth } from './contexts/AuthContext';
import progressSyncService from './services/ProgressSyncService';

const RoadmapDinamico = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [phases, setPhases] = useState([]);
  const [stats, setStats] = useState({
    completed: 0,
    testing: 0,
    inProgress: 0,
    globalProgress: 0,
    totalPhases: 10
  });
  const [collapsedPhases, setCollapsedPhases] = useState(new Set());
  const [selectedTask, setSelectedTask] = useState(null);
  const [checklistState, setChecklistState] = useState({});
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'kanban'
  const [showMetrics, setShowMetrics] = useState(false);
  const [backupHistory, setBackupHistory] = useState([]);
  const [staleNotifications, setStaleNotifications] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [syncStatus, setSyncStatus] = useState('idle'); // 'idle', 'syncing', 'synced', 'error'
  const { toggle: toggleSpeech, isSpeaking, currentTaskId } = useSpeech();
  const {
    toggleTimer,
    stopTimer,
    formatTime,
    formatHours,
    getTaskTime,
    getTotalWorkTime,
    isTaskActive,
    isTaskPaused,
    activeTaskId,
    sessionTime
  } = useWorkTimer();
  const { logActivity } = useActivityLog();
  const {
    addDependency,
    removeDependency,
    getDependencies,
    getDependents,
    isTaskBlocked,
    getAvailableDependencies
  } = useDependencies();
  const [showDependencyPicker, setShowDependencyPicker] = useState(false);

  // Load progress from Supabase or localStorage
  useEffect(() => {
    const loadProgress = async () => {
      setSyncStatus('syncing');

      // Try to load from Supabase if user is logged in
      if (user?.id) {
        try {
          // Check if user has remote progress
          const hasRemote = await progressSyncService.hasRemoteProgress(user.id);

          if (hasRemote) {
            // Load from Supabase
            const remoteData = await progressSyncService.loadProgress(user.id);
            if (remoteData) {
              const updatedPhases = roadmapData.map(phase => ({
                ...phase,
                tasks: phase.tasks.map(task => {
                  const savedTask = remoteData.taskStates.find(t => t.id === task.id);
                  return savedTask ? { ...task, status: savedTask.status } : task;
                })
              }));
              setPhases(updatedPhases);
              setChecklistState(remoteData.checklistStates);
              calculateStats(updatedPhases, remoteData.checklistStates);
              setSyncStatus('synced');
              console.log('✅ Loaded from Supabase');
              return;
            }
          } else {
            // First time - migrate localStorage to Supabase
            const migrated = await progressSyncService.migrateLocalToRemote(user.id);
            if (migrated) {
              console.log('📤 Migrated localStorage to Supabase');
            }
          }
        } catch (err) {
          console.error('Supabase load failed, falling back to localStorage:', err);
          setSyncStatus('error');
        }
      }

      // Fallback: Load from localStorage
      const savedState = localStorage.getItem('fixarr-roadmap-state-v6');
      const savedChecklist = localStorage.getItem('fixarr-roadmap-checklist-v1');

      let loadedChecklistState = {};
      if (savedChecklist) {
        try {
          loadedChecklistState = JSON.parse(savedChecklist);
        } catch (e) {
          console.error('Error loading checklist:', e);
        }
      }
      setChecklistState(loadedChecklistState);

      if (savedState) {
        try {
          const savedTasks = JSON.parse(savedState);
          const updatedPhases = roadmapData.map(phase => ({
            ...phase,
            tasks: phase.tasks.map(task => {
              const savedTask = savedTasks.find(t => t.id === task.id);
              return savedTask ? { ...task, status: savedTask.status } : task;
            })
          }));
          setPhases(updatedPhases);
          calculateStats(updatedPhases, loadedChecklistState);
        } catch (error) {
          console.error('Error loading saved state:', error);
          setPhases(roadmapData);
          calculateStats(roadmapData, loadedChecklistState);
        }
      } else {
        setPhases(roadmapData);
        calculateStats(roadmapData, loadedChecklistState);
      }
      setSyncStatus('idle');
    };

    loadProgress();

    // Force flush on page unload
    const handleUnload = () => {
      progressSyncService.forceFlush();
    };
    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, [user?.id]);

  // Save task state (localStorage + Supabase)
  const saveState = (updatedPhases, newChecklistState = checklistState) => {
    const allTasks = [];
    updatedPhases.forEach(phase => {
      phase.tasks.forEach(task => {
        allTasks.push({ id: task.id, status: task.status });

        // Sync to Supabase if user is logged in
        if (user?.id) {
          const taskChecklist = newChecklistState[task.id] || {};
          progressSyncService.saveTaskProgress(user.id, task.id, task.status, taskChecklist);
        }
      });
    });
    // Always save to localStorage as backup
    localStorage.setItem('fixarr-roadmap-state-v6', JSON.stringify(allTasks));
  };

  // Save checklist state (localStorage + Supabase)
  const saveChecklistState = (newChecklistState) => {
    localStorage.setItem('fixarr-roadmap-checklist-v1', JSON.stringify(newChecklistState));

    // Sync changed tasks to Supabase
    if (user?.id) {
      Object.keys(newChecklistState).forEach(taskId => {
        const task = phases.flatMap(p => p.tasks).find(t => t.id === taskId);
        const status = task?.status || 'pending';
        progressSyncService.saveTaskProgress(user.id, taskId, status, newChecklistState[taskId]);
      });
    }
  };

  // Calcular estadísticas
  const calculateStats = useCallback((phasesData, checklistData = checklistState) => {
    let totalTasks = 0;
    let completedTasks = 0;
    let testingTasks = 0;
    let inProgressTasks = 0;

    phasesData.forEach(phase => {
      phase.tasks.forEach(task => {
        totalTasks++;

        // Check if task has checklist and calculate real status
        if (task.checklist && task.checklist.length > 0) {
          const taskChecklist = checklistData[task.id] || {};
          const completedItems = task.checklist.filter(item => taskChecklist[item.id]).length;

          if (completedItems === task.checklist.length) {
            completedTasks++;
          } else if (completedItems > 0) {
            testingTasks++;
          } else if (task.status === 'testing') {
            testingTasks++;
          } else if (task.status === 'progress') {
            inProgressTasks++;
          }
        } else {
          if (task.status === 'completed') completedTasks++;
          if (task.status === 'testing') testingTasks++;
          if (task.status === 'progress') inProgressTasks++;
        }
      });
    });

    const globalProgress = Math.round((completedTasks / totalTasks) * 100);

    setStats({
      completed: completedTasks,
      testing: testingTasks,
      inProgress: inProgressTasks,
      globalProgress: globalProgress,
      totalPhases: phasesData.length
    });
  }, [checklistState]);

  // Toggle checklist item
  const toggleChecklistItem = (taskId, itemId) => {
    const wasCompleted = checklistState[taskId]?.[itemId] || false;
    const newState = {
      ...checklistState,
      [taskId]: {
        ...(checklistState[taskId] || {}),
        [itemId]: !wasCompleted
      }
    };
    setChecklistState(newState);
    saveChecklistState(newState);
    calculateStats(phases, newState);

    // Log activity
    const task = phases.flatMap(p => p.tasks).find(t => t.id === taskId);
    const item = task?.checklist?.find(i => i.id === itemId);
    if (task && item) {
      logActivity({
        type: 'checklist_item',
        action: wasCompleted ? 'uncompleted' : 'completed',
        taskId,
        taskName: task.name,
        details: {
          itemId,
          itemText: item.text
        }
      });
    }
  };

  // Get checklist progress for a task
  const getChecklistProgress = (task) => {
    if (!task.checklist || task.checklist.length === 0) return null;

    const taskChecklist = checklistState[task.id] || {};
    const completed = task.checklist.filter(item => taskChecklist[item.id]).length;
    const total = task.checklist.length;
    const percentage = Math.round((completed / total) * 100);

    return { completed, total, percentage };
  };

  // Get real task status based on checklist
  const getRealStatus = (task) => {
    if (!task.checklist || task.checklist.length === 0) {
      return task.status;
    }

    const progress = getChecklistProgress(task);
    if (progress.percentage === 100) return 'completed';
    if (progress.completed > 0) return 'testing';
    if (task.status === 'testing' || task.status === 'completed') return 'testing';
    return task.status;
  };

  // Get all tasks grouped by status for Kanban view
  const getKanbanGroups = () => {
    const groups = {
      pending: [],
      progress: [],
      testing: [],
      completed: []
    };

    phases.forEach((phase, phaseIndex) => {
      phase.tasks.forEach(task => {
        const status = getRealStatus(task);
        if (groups[status]) {
          groups[status].push({ ...task, phaseIndex, phaseName: phase.name });
        }
      });
    });

    return groups;
  };

  // Calculate metrics
  const getMetrics = () => {
    const allTasks = phases.flatMap((p, pi) => p.tasks.map(t => ({ ...t, phaseIndex: pi })));
    const completedTasks = allTasks.filter(t => getRealStatus(t) === 'completed');
    const testingTasks = allTasks.filter(t => getRealStatus(t) === 'testing');

    // Get work times from localStorage
    const workTimes = JSON.parse(localStorage.getItem('fixarr-work-times') || '{}');

    const totalTimeSpent = Object.values(workTimes).reduce((a, b) => a + b, 0);
    const totalEstimated = allTasks.reduce((a, t) => a + (t.estimatedTime || 0) * 60, 0);
    const avgTimePerTask = completedTasks.length > 0 ? totalTimeSpent / completedTasks.length : 0;

    // Tasks per week (assuming started 4 weeks ago)
    const tasksPerWeek = Math.round(completedTasks.length / 4);

    return {
      totalTasks: allTasks.length,
      completedCount: completedTasks.length,
      testingCount: testingTasks.length,
      totalTimeSpent,
      totalEstimated,
      avgTimePerTask,
      tasksPerWeek,
      efficiency: totalEstimated > 0 ? Math.round((totalTimeSpent / totalEstimated) * 100) : 0
    };
  };

  // Check for stale testing tasks (in testing for too long)
  useEffect(() => {
    const checkStaleTasks = () => {
      const testingTasks = phases.flatMap((p, pi) =>
        p.tasks.filter(t => getRealStatus(t) === 'testing')
          .map(t => ({ ...t, phaseIndex: pi, phaseName: p.name }))
      );

      // For demo, mark tasks with low checklist progress as stale
      const staleTasks = testingTasks.filter(task => {
        const progress = getChecklistProgress(task);
        return progress && progress.percentage < 50;
      });

      setStaleNotifications(staleTasks.slice(0, 3)); // Max 3 notifications
    };

    if (phases.length > 0) {
      checkStaleTasks();
    }
  }, [phases, checklistState]);

  // Load backup history
  useEffect(() => {
    const savedHistory = localStorage.getItem('fixarr-backup-history');
    if (savedHistory) {
      setBackupHistory(JSON.parse(savedHistory));
    }
  }, []);

  // Save backup to history
  const addBackupToHistory = () => {
    const newBackup = {
      id: Date.now(),
      date: new Date().toISOString(),
      branch: `roadmap_backup_${new Date().toLocaleDateString('es-ES').replace(/\//g, '')}`,
      success: true
    };
    const updated = [newBackup, ...backupHistory].slice(0, 10); // Keep last 10
    setBackupHistory(updated);
    localStorage.setItem('fixarr-backup-history', JSON.stringify(updated));
  };


  // Cambiar estado de tarea
  const changeTaskStatus = (e, phaseIndex, taskId) => {
    e.stopPropagation();
    const updatedPhases = [...phases];
    const task = updatedPhases[phaseIndex].tasks.find(t => t.id === taskId);

    if (task) {
      const statusCycle = ['pending', 'progress', 'testing', 'completed'];
      const currentIndex = statusCycle.indexOf(task.status);
      const oldStatus = task.status;
      task.status = statusCycle[(currentIndex + 1) % statusCycle.length];

      setPhases(updatedPhases);
      calculateStats(updatedPhases, checklistState);
      saveState(updatedPhases);

      // Log activity
      logActivity({
        type: 'status_change',
        action: 'changed',
        taskId,
        taskName: task.name,
        details: {
          from: oldStatus,
          to: task.status
        }
      });
    }
  };

  // Calcular progreso de fase (con checklist awareness)
  const calculatePhaseProgress = (phase) => {
    const total = phase.tasks.length;
    let completed = 0;
    let testing = 0;
    let totalChecklistItems = 0;
    let completedChecklistItems = 0;

    phase.tasks.forEach(task => {
      const realStatus = getRealStatus(task);
      if (realStatus === 'completed') completed++;
      if (realStatus === 'testing') testing++;

      // Count checklist items
      if (task.checklist) {
        const taskChecklist = checklistState[task.id] || {};
        task.checklist.forEach(item => {
          totalChecklistItems++;
          if (taskChecklist[item.id]) completedChecklistItems++;
        });
      }
    });

    const percentage = Math.round((completed / total) * 100);
    const checklistPercentage = totalChecklistItems > 0
      ? Math.round((completedChecklistItems / totalChecklistItems) * 100)
      : percentage;

    return { completed, total, percentage, testing, checklistPercentage };
  };

  // Toggle colapsar fase
  const togglePhase = (phaseIndex) => {
    const newCollapsed = new Set(collapsedPhases);
    if (newCollapsed.has(phaseIndex)) {
      newCollapsed.delete(phaseIndex);
    } else {
      newCollapsed.add(phaseIndex);
    }
    setCollapsedPhases(newCollapsed);
  };

  // Abrir modal de tarea
  const openTaskModal = (task, phaseIndex) => {
    setSelectedTask({ ...task, phaseIndex });
  };

  // Cerrar modal
  const closeModal = () => {
    setSelectedTask(null);
  };

  // Copiar progreso
  const copyProgress = () => {
    const testingTasks = [];
    phases.forEach(phase => {
      phase.tasks.forEach(task => {
        const realStatus = getRealStatus(task);
        if (realStatus === 'testing' || realStatus === 'progress') {
          const progress = getChecklistProgress(task);
          const progressStr = progress ? ` (${progress.percentage}%)` : '';
          testingTasks.push(`- ${task.id} - ${task.name}${progressStr}`);
        }
      });
    });

    const text = `FIXARR ROADMAP - ${new Date().toLocaleDateString('es-ES')}

📊 PROGRESO: ${stats.globalProgress}%
✅ Completadas: ${stats.completed}
🧪 Testing: ${stats.testing}
🔄 En Progreso: ${stats.inProgress}

TAREAS ACTUALES:
${testingTasks.length > 0 ? testingTasks.join('\n') : 'Ninguna'}`;

    navigator.clipboard.writeText(text).then(() => {
      alert('¡Progreso copiado al portapapeles!');
    });
  };

  // Renderizar badge de estado
  const getStatusBadge = (status, progress = null) => {
    const badges = {
      completed: { icon: '✅', text: 'Verificado', class: 'status-completed' },
      testing: { icon: '🧪', text: progress ? `Testing ${progress.percentage}%` : 'Testing', class: 'status-testing' },
      progress: { icon: '🔄', text: 'En Desarrollo', class: 'status-progress' },
      pending: { icon: '📦', text: 'Pendiente', class: 'status-pending' }
    };
    const badge = badges[status] || badges.pending;
    return (
      <span className={`status-badge ${badge.class}`}>
        {badge.icon} {badge.text}
      </span>
    );
  };

  // Get status icon for task card
  const getStatusIcon = (task) => {
    const realStatus = getRealStatus(task);
    const progress = getChecklistProgress(task);

    if (realStatus === 'completed') return '✅';
    if (realStatus === 'testing') {
      return progress ? `🧪` : '🧪';
    }
    if (realStatus === 'progress') return '🔄';
    return '⬜';
  };

  // Get all tasks as flat array (for dependencies)
  const getAllTasks = useCallback(() => {
    return phases.flatMap((phase, phaseIndex) =>
      phase.tasks.map(task => ({
        ...task,
        phaseIndex,
        phaseName: phase.name,
        realStatus: getRealStatus(task)
      }))
    );
  }, [phases, checklistState]);

  // Check if a task is blocked by dependencies
  const getTaskBlockStatus = useCallback((taskId) => {
    const allTasks = getAllTasks();
    return isTaskBlocked(taskId, allTasks);
  }, [getAllTasks, isTaskBlocked]);

  // Get task by ID
  const getTaskById = useCallback((taskId) => {
    return getAllTasks().find(t => t.id === taskId);
  }, [getAllTasks]);

  // Handle adding a dependency
  const handleAddDependency = (taskId, dependsOnId) => {
    const result = addDependency(taskId, dependsOnId);
    if (result.success) {
      const task = getTaskById(taskId);
      const depTask = getTaskById(dependsOnId);
      logActivity({
        type: 'dependency',
        action: 'added',
        taskId,
        taskName: task?.name || taskId,
        details: {
          dependsOn: dependsOnId,
          dependsOnName: depTask?.name || dependsOnId
        }
      });
    } else {
      alert(result.error);
    }
  };

  // Handle removing a dependency
  const handleRemoveDependency = (taskId, dependsOnId) => {
    removeDependency(taskId, dependsOnId);
    const task = getTaskById(taskId);
    const depTask = getTaskById(dependsOnId);
    logActivity({
      type: 'dependency',
      action: 'removed',
      taskId,
      taskName: task?.name || taskId,
      details: {
        dependsOn: dependsOnId,
        dependsOnName: depTask?.name || dependsOnId
      }
    });
  };

  return (
    <div className="roadmap-container">
      {/* Animated Background */}
      <div className="god-mode-bg">
        <div className="grid-overlay"></div>
        <div className="glow-orb orb-1"></div>
        <div className="glow-orb orb-2"></div>
        <div className="glow-orb orb-3"></div>
      </div>

      {/* Header */}
      <div className="roadmap-header">
        <div className="header-left">
          <button className="btn-back" onClick={() => navigate('/dashboard')} title="Volver al Dashboard">
            ← Salir
          </button>
          <div className="logo-container">
            <span className="logo-icon">⚡</span>
            <div>
              <h1>FIXARR ROADMAP</h1>
            </div>
          </div>
        </div>

        {/* Compact Stats Bar */}
        <div className="stats-bar">
          <div className="stat-mini stat-completed">
            <span className="stat-icon">✅</span>
            <span className="stat-value">{stats.completed}</span>
          </div>
          <div className="stat-mini stat-testing">
            <span className="stat-icon">🧪</span>
            <span className="stat-value">{stats.testing}</span>
          </div>
          <div className="stat-mini stat-progress">
            <span className="stat-icon">🔄</span>
            <span className="stat-value">{stats.inProgress}</span>
          </div>
          <div className="stat-mini stat-global">
            <span className="stat-icon">📊</span>
            <span className="stat-value">{stats.globalProgress}%</span>
          </div>
        </div>

        {/* Activity Feed Widget */}
        <ActivityFeed />

        {/* Hamburger Menu Button */}
        <button
          className={`hamburger-btn ${menuOpen ? 'open' : ''}`}
          onClick={() => setMenuOpen(!menuOpen)}
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
      </div>

      {/* Slide-out Sidebar */}
      {menuOpen && (
        <>
          <div className="sidebar-overlay" onClick={() => setMenuOpen(false)}></div>
          <div className="sidebar-menu">
            <div className="sidebar-header">
              <h3>Menú</h3>
              <button className="sidebar-close" onClick={() => setMenuOpen(false)}>✕</button>
            </div>
            <nav className="sidebar-nav">
              <button
                className="sidebar-item"
                onClick={() => { setViewMode(viewMode === 'list' ? 'kanban' : 'list'); setMenuOpen(false); }}
              >
                <span className="sidebar-icon">{viewMode === 'list' ? '📊' : '📋'}</span>
                <span>{viewMode === 'list' ? 'Vista Kanban' : 'Vista Lista'}</span>
              </button>
              <button
                className="sidebar-item"
                onClick={() => { setShowMetrics(!showMetrics); setMenuOpen(false); }}
              >
                <span className="sidebar-icon">📈</span>
                <span>Métricas</span>
              </button>
              <div className="sidebar-divider"></div>
              <Link to="/brand-book" className="sidebar-item" onClick={() => setMenuOpen(false)}>
                <span className="sidebar-icon">🎨</span>
                <span>Brand Book</span>
              </Link>
              <Link to="/vault" className="sidebar-item" onClick={() => setMenuOpen(false)}>
                <span className="sidebar-icon">🔐</span>
                <span>Vault</span>
              </Link>
              <Link to="/docs" className="sidebar-item" onClick={() => setMenuOpen(false)}>
                <span className="sidebar-icon">📖</span>
                <span>Documentación</span>
              </Link>
              <Link to="/activity" className="sidebar-item" onClick={() => setMenuOpen(false)}>
                <span className="sidebar-icon">📜</span>
                <span>Historial</span>
              </Link>
              <div className="sidebar-divider"></div>
              <button className="sidebar-item" onClick={() => { copyProgress(); setMenuOpen(false); }}>
                <span className="sidebar-icon">📋</span>
                <span>Copiar Progreso</span>
              </button>
              <button className="sidebar-item sidebar-item-muted" onClick={() => navigate('/dashboard')}>
                <span className="sidebar-icon">←</span>
                <span>Volver al Dashboard</span>
              </button>
            </nav>
          </div>
        </>
      )}

      {/* Global Progress Bar - GOD MODE */}
      <div className="global-progress-container">
        <div className="global-progress-bar">
          <div
            className="global-progress-fill"
            style={{ width: `${stats.globalProgress}%` }}
          >
            <div className="progress-glow"></div>
            <div className="progress-particles"></div>
          </div>
          <div className="progress-markers">
            {[25, 50, 75].map(mark => (
              <div
                key={mark}
                className={`progress-marker ${stats.globalProgress >= mark ? 'passed' : ''}`}
                style={{ left: `${mark}%` }}
              >
                <span>{mark}%</span>
              </div>
            ))}
          </div>
        </div>
        <div className="progress-label">
          <span className="progress-text">Progreso Global</span>
          <span className="progress-value">{stats.globalProgress}%</span>
        </div>
      </div>

      {/* Metrics Panel */}
      {showMetrics && (
        <div className="metrics-panel">
          <div className="metrics-header">
            <h3>📈 Métricas de Rendimiento</h3>
            <button className="close-metrics" onClick={() => setShowMetrics(false)}>✕</button>
          </div>
          <div className="metrics-grid">
            <div className="metric-card">
              <span className="metric-icon">🎯</span>
              <div className="metric-value">{getMetrics().completedCount}/{getMetrics().totalTasks}</div>
              <div className="metric-label">Tareas Completadas</div>
            </div>
            <div className="metric-card">
              <span className="metric-icon">📅</span>
              <div className="metric-value">{getMetrics().tasksPerWeek}</div>
              <div className="metric-label">Tareas/Semana</div>
            </div>
            <div className="metric-card">
              <span className="metric-icon">⏱️</span>
              <div className="metric-value">{formatHours(getMetrics().avgTimePerTask)}</div>
              <div className="metric-label">Tiempo Promedio/Tarea</div>
            </div>
            <div className="metric-card">
              <span className="metric-icon">⚡</span>
              <div className="metric-value">{getMetrics().efficiency || '--'}%</div>
              <div className="metric-label">Eficiencia (vs Estimado)</div>
            </div>
          </div>

          {/* Backup History */}
          {backupHistory.length > 0 && (
            <div className="backup-history">
              <h4>📦 Historial de Backups</h4>
              <div className="backup-list">
                {backupHistory.slice(0, 5).map(backup => (
                  <div key={backup.id} className="backup-item">
                    <span className="backup-status">{backup.success ? '✅' : '❌'}</span>
                    <span className="backup-branch">{backup.branch}</span>
                    <span className="backup-date">{new Date(backup.date).toLocaleDateString('es-ES')}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}


      {/* Kanban View */}
      {viewMode === 'kanban' && (
        <div className="kanban-container">
          {['pending', 'progress', 'testing', 'completed'].map(status => {
            const statusLabels = {
              pending: { icon: '📋', label: 'Pendiente', color: '#64748b' },
              progress: { icon: '🔄', label: 'En Progreso', color: '#3b82f6' },
              testing: { icon: '🧪', label: 'Testing', color: '#fbbf24' },
              completed: { icon: '✅', label: 'Verificadas', color: '#22c55e' }
            };
            const tasks = getKanbanGroups()[status];
            const { icon, label, color } = statusLabels[status];

            return (
              <div key={status} className={`kanban-column kanban-${status}`}>
                <div className="kanban-header" style={{ borderColor: color }}>
                  <span className="kanban-icon">{icon}</span>
                  <span className="kanban-title">{label}</span>
                  <span className="kanban-count" style={{ background: color }}>{tasks.length}</span>
                </div>
                <div className="kanban-tasks">
                  {tasks.map(task => (
                    <div
                      key={task.id}
                      className={`kanban-task ${task.highlight ? 'highlight' : ''}`}
                      onClick={() => openTaskModal(task, task.phaseIndex)}
                    >
                      <div className="kanban-task-id">{task.id}</div>
                      <div className="kanban-task-name">{task.name}</div>
                      <div className="kanban-task-phase">{task.phaseName}</div>
                      {task.checklist && (
                        <div className="kanban-task-progress">
                          <div
                            className="kanban-progress-fill"
                            style={{ width: `${getChecklistProgress(task)?.percentage || 0}%` }}
                          />
                        </div>
                      )}
                    </div>
                  ))}
                  {tasks.length === 0 && (
                    <div className="kanban-empty">Sin tareas</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Phases (List View) */}
      {viewMode === 'list' && (
        <div className="phases-container">
          {phases.map((phase, phaseIndex) => {
            const progress = calculatePhaseProgress(phase);
            const isCollapsed = collapsedPhases.has(phaseIndex);

            return (
              <div
                key={phaseIndex}
                className={`phase ${phase.isNew ? 'phase-new' : ''} ${isCollapsed ? 'collapsed' : ''} ${progress.percentage === 100 ? 'phase-complete' : ''}`}
              >
                <div
                  className="phase-header"
                  onClick={() => togglePhase(phaseIndex)}
                >
                  <div className="phase-title">
                    <span className={`toggle-icon ${isCollapsed ? 'collapsed' : ''}`}>▼</span>
                    <h2>{phase.name}</h2>
                    {phase.isNew && <span className="new-badge">⭐ NUEVA</span>}
                  </div>
                  <div className="phase-meta">
                    <span className="phase-count">{progress.completed}/{progress.total}</span>
                    {progress.testing > 0 && (
                      <span className="phase-testing-badge">🧪 {progress.testing}</span>
                    )}
                    {/* GOD MODE Progress Bar */}
                    <div className="phase-progress-wrapper">
                      <div className="phase-progress-bar">
                        <div
                          className="phase-progress-fill"
                          style={{ width: `${progress.percentage}%` }}
                        >
                          <div className="phase-progress-shine"></div>
                        </div>
                        {progress.testing > 0 && (
                          <div
                            className="phase-progress-testing"
                            style={{
                              width: `${(progress.testing / progress.total) * 100}%`,
                              left: `${progress.percentage}%`
                            }}
                          />
                        )}
                      </div>
                      <span className="phase-percentage">{progress.percentage}%</span>
                    </div>
                  </div>
                </div>

                {!isCollapsed && (
                  <div className="phase-content">
                    {phase.tasks.map((task) => {
                      const realStatus = getRealStatus(task);
                      const taskProgress = getChecklistProgress(task);
                      const blockStatus = getTaskBlockStatus(task.id);
                      const taskDeps = getDependencies(task.id);
                      const taskDependents = getDependents(task.id);

                      return (
                        <div
                          key={task.id}
                          className={`task ${task.highlight ? 'highlight' : ''} status-${realStatus} ${blockStatus.blocked ? 'task-blocked' : ''}`}
                          onClick={() => openTaskModal(task, phaseIndex)}
                        >
                          <div className="task-main">
                            <span className="task-id">{task.id}</span>
                            <span className="task-name">{task.name}</span>
                            {/* Blocked indicator */}
                            {blockStatus.blocked && (
                              <span className="task-blocked-badge" title={`Bloqueada por: ${blockStatus.blockedBy.map(t => t.name).join(', ')}`}>
                                🔒 Bloqueada
                              </span>
                            )}
                            {/* Dependencies indicator */}
                            {taskDeps.length > 0 && !blockStatus.blocked && (
                              <span className="task-deps-badge" title={`Depende de ${taskDeps.length} tarea(s)`}>
                                🔗 {taskDeps.length}
                              </span>
                            )}
                            {/* Dependents indicator */}
                            {taskDependents.length > 0 && (
                              <span className="task-dependents-badge" title={`${taskDependents.length} tarea(s) dependen de esta`}>
                                ⬇️ {taskDependents.length}
                              </span>
                            )}
                            {task.checklist && (
                              <span className="task-checklist-badge" title="Tiene checklist de verificación">
                                📋 {taskProgress?.completed || 0}/{task.checklist.length}
                              </span>
                            )}
                          </div>
                          <div className="task-actions">
                            {/* Inline Timer Button */}
                            {realStatus !== 'completed' && realStatus !== 'pending' && (
                              <div className={`task-timer ${isTaskActive(task.id) ? 'active' : ''}`}>
                                <button
                                  className={`timer-btn ${isTaskActive(task.id) ? (isTaskPaused(task.id) ? 'paused' : 'running') : ''}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleTimer(task.id);
                                  }}
                                  title={isTaskActive(task.id) ? (isTaskPaused(task.id) ? 'Continuar' : 'Pausar') : 'Empezar'}
                                >
                                  {isTaskActive(task.id) ? (isTaskPaused(task.id) ? '▶️' : '⏸️') : '▶️'}
                                </button>
                                {isTaskActive(task.id) && (
                                  <span className="timer-display">
                                    {formatTime(getTaskTime(task.id))}
                                  </span>
                                )}
                                {!isTaskActive(task.id) && getTaskTime(task.id) > 0 && (
                                  <span className="timer-display saved">
                                    {formatTime(getTaskTime(task.id))}
                                  </span>
                                )}
                              </div>
                            )}
                            {/* Mini progress bar for tasks with checklist */}
                            {taskProgress && (
                              <div className="task-mini-progress">
                                <div
                                  className="task-mini-progress-fill"
                                  style={{ width: `${taskProgress.percentage}%` }}
                                />
                              </div>
                            )}
                            {task.tech && (
                              <span className="task-tech">{task.tech.split(',')[0]}</span>
                            )}
                            <button
                              className={`status-btn status-${realStatus}`}
                              onClick={(e) => changeTaskStatus(e, phaseIndex, task.id)}
                            >
                              {getStatusIcon(task)}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Footer */}
      <div className="footer">
        <div className="footer-content">
          <span className="footer-logo">⚡ FIXARR</span>
          <span className="footer-version">v6.0 GOD MODE</span>
          <span className="footer-stats">{stats.completed} verificadas • {stats.globalProgress}% completado</span>
        </div>
      </div>

      {/* Task Detail Modal with Checklist */}
      {selectedTask && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content modal-god-mode" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeModal}>×</button>

            <div className="modal-header">
              <span className="modal-id">{selectedTask.id}</span>
              <h2>{selectedTask.name}</h2>
              {getStatusBadge(getRealStatus(selectedTask), getChecklistProgress(selectedTask))}
            </div>

            <div className="modal-body">
              {selectedTask.description && (
                <div className="modal-section">
                  <div className="modal-section-header">
                    <h3>📋 Descripción</h3>
                    <button
                      className={`speak-btn ${isSpeaking && currentTaskId === selectedTask.id ? 'speaking' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleSpeech(selectedTask.description, selectedTask.id);
                      }}
                      title={isSpeaking && currentTaskId === selectedTask.id ? 'Detener' : 'Leer en voz alta'}
                    >
                      {isSpeaking && currentTaskId === selectedTask.id ? '🔊' : '🔈'}
                    </button>
                  </div>
                  <p>{selectedTask.description}</p>
                </div>
              )}

              {/* CHECKLIST SECTION - GOD MODE */}
              {selectedTask.checklist && selectedTask.checklist.length > 0 && (
                <div className="modal-section checklist-section">
                  <h3>✅ Checklist de Verificación</h3>
                  <div className="checklist-progress">
                    <div className="checklist-progress-bar">
                      <div
                        className="checklist-progress-fill"
                        style={{ width: `${getChecklistProgress(selectedTask)?.percentage || 0}%` }}
                      >
                        <div className="checklist-progress-glow"></div>
                      </div>
                    </div>
                    <span className="checklist-progress-text">
                      {getChecklistProgress(selectedTask)?.completed || 0} / {selectedTask.checklist.length} completados
                    </span>
                  </div>
                  <div className="checklist-items">
                    {selectedTask.checklist.map((item) => {
                      const isChecked = checklistState[selectedTask.id]?.[item.id] || false;
                      return (
                        <div
                          key={item.id}
                          className={`checklist-item ${isChecked ? 'checked' : ''}`}
                          onClick={() => toggleChecklistItem(selectedTask.id, item.id)}
                        >
                          <div className="checklist-checkbox">
                            {isChecked ? '✅' : '⬜'}
                          </div>
                          <span className="checklist-text">{item.text}</span>
                        </div>
                      );
                    })}
                  </div>
                  {getChecklistProgress(selectedTask)?.percentage === 100 && (
                    <div className="checklist-complete-banner">
                      <span>🎉</span>
                      <span>¡Verificación Completa!</span>
                      <span>🎉</span>
                    </div>
                  )}
                </div>
              )}

              {selectedTask.tech && (
                <div className="modal-section">
                  <h3>🛠️ Tecnologías</h3>
                  <div className="tech-tags">
                    {selectedTask.tech.split(',').map((t, i) => (
                      <span key={i} className="tech-tag">{t.trim()}</span>
                    ))}
                  </div>
                </div>
              )}

              {selectedTask.time && (
                <div className="modal-section">
                  <h3>⏱️ Tiempo Estimado</h3>
                  <p>{selectedTask.time}</p>
                </div>
              )}

              {/* Dependencies Section */}
              <div className="modal-section dependencies-section">
                <div className="dependencies-header">
                  <h3>🔗 Dependencias</h3>
                  <button
                    className="btn-add-dependency"
                    onClick={() => setShowDependencyPicker(!showDependencyPicker)}
                  >
                    {showDependencyPicker ? '✕ Cerrar' : '+ Añadir'}
                  </button>
                </div>

                {/* Block status warning */}
                {(() => {
                  const blockStatus = getTaskBlockStatus(selectedTask.id);
                  if (blockStatus.blocked) {
                    return (
                      <div className="dependency-blocked-warning">
                        <span className="blocked-icon">🔒</span>
                        <div className="blocked-info">
                          <strong>Esta tarea está bloqueada</strong>
                          <p>Completa primero: {blockStatus.blockedBy.map(t => t.name).join(', ')}</p>
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}

                {/* Current dependencies */}
                <div className="dependencies-list">
                  {getDependencies(selectedTask.id).length > 0 ? (
                    <>
                      <p className="dependencies-label">Esta tarea depende de:</p>
                      {getDependencies(selectedTask.id).map(depId => {
                        const depTask = getTaskById(depId);
                        if (!depTask) return null;
                        return (
                          <div key={depId} className={`dependency-item ${depTask.realStatus === 'completed' ? 'completed' : 'pending'}`}>
                            <span className="dep-status">
                              {depTask.realStatus === 'completed' ? '✅' : '⏳'}
                            </span>
                            <span className="dep-id">{depTask.id}</span>
                            <span className="dep-name">{depTask.name}</span>
                            <button
                              className="btn-remove-dep"
                              onClick={() => handleRemoveDependency(selectedTask.id, depId)}
                              title="Eliminar dependencia"
                            >
                              ✕
                            </button>
                          </div>
                        );
                      })}
                    </>
                  ) : (
                    <p className="no-dependencies">Sin dependencias configuradas</p>
                  )}
                </div>

                {/* Tasks that depend on this one */}
                {getDependents(selectedTask.id).length > 0 && (
                  <div className="dependents-list">
                    <p className="dependencies-label">Tareas que dependen de esta:</p>
                    {getDependents(selectedTask.id).map(depId => {
                      const depTask = getTaskById(depId);
                      if (!depTask) return null;
                      return (
                        <div key={depId} className="dependent-item">
                          <span className="dep-icon">⬇️</span>
                          <span className="dep-id">{depTask.id}</span>
                          <span className="dep-name">{depTask.name}</span>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Dependency picker */}
                {showDependencyPicker && (
                  <div className="dependency-picker">
                    <p className="picker-label">Selecciona tarea de la que depende:</p>
                    <div className="picker-list">
                      {getAvailableDependencies(selectedTask.id, getAllTasks())
                        .filter(t => !getDependencies(selectedTask.id).includes(t.id))
                        .map(task => (
                          <div
                            key={task.id}
                            className="picker-item"
                            onClick={() => {
                              handleAddDependency(selectedTask.id, task.id);
                              setShowDependencyPicker(false);
                            }}
                          >
                            <span className="picker-status">
                              {task.realStatus === 'completed' ? '✅' :
                                task.realStatus === 'testing' ? '🧪' :
                                  task.realStatus === 'progress' ? '🔄' : '⬜'}
                            </span>
                            <span className="picker-id">{task.id}</span>
                            <span className="picker-name">{task.name}</span>
                            <span className="picker-phase">{task.phaseName}</span>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Observations Section */}
              <Observations
                taskId={selectedTask.id}
                taskName={selectedTask.name}
              />
            </div>

            <div className="modal-actions">
              <Link
                to={`/docs/${selectedTask.id}`}
                className="btn btn-docs"
                onClick={closeModal}
              >
                📖 Ver explicación completa
              </Link>
              <button
                className="btn btn-primary"
                onClick={(e) => {
                  changeTaskStatus(e, selectedTask.phaseIndex, selectedTask.id);
                  // Update selected task to reflect new status
                  const updatedTask = phases[selectedTask.phaseIndex].tasks.find(t => t.id === selectedTask.id);
                  if (updatedTask) {
                    setSelectedTask({ ...updatedTask, phaseIndex: selectedTask.phaseIndex });
                  }
                }}
              >
                Cambiar Estado
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoadmapDinamico;
